import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useDebouncedValue } from "hooks";
import moment from "moment";
import {
  ChatView,
  ChatMessage,
  ConversationContact,
  ConversationsList,
  ConversationsStatusFilter,
  MessageDirection,
} from "modules/aiConversations";
import { useProfile, useSupportConversationsCounter } from "modules/common";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useGetConversationMessages,
  useGetConversations,
  useMarkReadConversation,
  useSendMessage,
  useSetConversationMode,
} from "../../apis";
import { ConversationMode } from "../../apis/useSetConversationMode";
import {
  InAppConversationStatus,
  InAppMessageDirection,
  InAppMessageSender,
  InAppMessageType,
  SUPPORT_QUERY_KEY,
} from "../../constants/enums";
import {
  buildOptimisticId,
  insertOrReplaceMessage,
  removeMessageById,
} from "../../helpers";
import {
  AiTypingProvider,
  useAiTyping,
  useSupportRealTimeUpdate,
} from "../../hooks";
import {
  InAppConversation,
  InAppConversationMessagesResponse,
  InAppConversationsListResponse,
  InAppMessage,
} from "../../support.types";

import styles from "./Support.module.scss";

type ConversationsInfinite = InfiniteData<InAppConversationsListResponse>;
type MessagesInfinite = InfiniteData<InAppConversationMessagesResponse>;

const formatDate = (iso: string | null) => {
  if (!iso) return "";
  const m = moment(iso);
  if (!m.isValid()) return "";
  return m.fromNow();
};

const formatTime = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const buildResellerName = (conversation: InAppConversation): string => {
  const r = conversation.reseller;
  if (!r) return conversation.resellerId;
  const full = `${r.firstname ?? ""} ${r.lastname ?? ""}`.trim();
  return full.length > 0 ? full : conversation.resellerId;
};

const toConversationContact = (
  conversation: InAppConversation,
): ConversationContact => ({
  id: conversation.id,
  name: buildResellerName(conversation),
  channel: "WhatsApp",
  avatarUrl: conversation.reseller?.image?.url,
  preview: conversation.lastMessage?.text ?? "",
  date: formatDate(
    conversation.lastMessage?.createdAt ??
      conversation.lastInboundAt ??
      conversation.updatedAt,
  ),
  unreadCount: conversation.unreadCount ?? 0,
});

const FALLBACK_LABELS: Record<InAppMessageType, string> = {
  [InAppMessageType.Text]: "",
  [InAppMessageType.Image]: "[image]",
  [InAppMessageType.Voice]: "[voice]",
  [InAppMessageType.Document]: "[file]",
  [InAppMessageType.Video]: "[video]",
};

const toChatMessage = (message: InAppMessage): ChatMessage => {
  const isCustomer = message.sender === InAppMessageSender.Customer;
  const isAi = message.sender === InAppMessageSender.Ai;
  const base = {
    id: message.id,
    direction: (isCustomer ? "incoming" : "outgoing") as MessageDirection,
    isAI: isAi,
    time: formatTime(message.createdAt),
  };

  if (message.messageType === InAppMessageType.Image && message.mediaUrl) {
    return {
      ...base,
      variant: "image",
      mediaUrl: message.mediaUrl,
      text: message.text ?? undefined,
    };
  }

  if (message.messageType === InAppMessageType.Voice && message.mediaUrl) {
    return { ...base, variant: "audio", mediaUrl: message.mediaUrl };
  }

  if (message.messageType === InAppMessageType.Video && message.mediaUrl) {
    return {
      ...base,
      variant: "video",
      mediaUrl: message.mediaUrl,
      text: message.text ?? undefined,
    };
  }

  if (message.text) {
    return { ...base, variant: "text", text: message.text };
  }

  return {
    ...base,
    variant: "text",
    text: FALLBACK_LABELS[message.messageType],
  };
};

const toToggleMode = (status: InAppConversationStatus): ConversationMode =>
  status === InAppConversationStatus.Human
    ? InAppConversationStatus.Human
    : InAppConversationStatus.Ai;

const toListStatus = (
  filter: ConversationsStatusFilter,
): InAppConversationStatus | undefined => {
  if (filter === "all") return undefined;
  switch (filter) {
    case "ai":
      return InAppConversationStatus.Ai;
    case "human":
      return InAppConversationStatus.Human;
    case "closed":
      return InAppConversationStatus.Closed;
    default:
      return undefined;
  }
};

const Support = () => (
  <AiTypingProvider>
    <SupportContent />
  </AiTypingProvider>
);

const SupportContent = () => {
  const { t } = useTranslation();
  const [selectedConversationId, setSelectedConversationId] = useState<
    string | null
  >(null);

  const queryClient = useQueryClient();
  useSupportRealTimeUpdate();
  const { isTyping } = useAiTyping();
  const { user } = useProfile({ enabled: false });
  const currentAdminId = user.id ?? null;
  const { decrementSupportUnread, setOpenSupportConversation } =
    useSupportConversationsCounter();

  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] =
    useState<ConversationsStatusFilter>("all");
  const debouncedSearch = useDebouncedValue(searchInput, 400);
  const trimmedSearch = debouncedSearch.trim();

  const {
    conversations: serverConversations,
    fetchNextPage: fetchNextConversationsPage,
    hasNextPage: hasMoreConversations,
    isFetchingNextPage: isLoadingMoreConversations,
    isLoading: isLoadingConversations,
  } = useGetConversations({
    status: toListStatus(statusFilter),
    search: trimmedSearch || undefined,
  });

  const conversations = serverConversations ?? [];

  useEffect(() => {
    if (conversations.length === 0) return;
    if (
      !selectedConversationId ||
      !conversations.some((c) => c.id === selectedConversationId)
    ) {
      setSelectedConversationId(conversations[0].id);
    }
  }, [conversations, selectedConversationId]);

  const conversationContacts = useMemo(
    () => conversations.map(toConversationContact),
    [conversations],
  );

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedConversationId),
    [conversations, selectedConversationId],
  );

  const selectedContact = useMemo(
    () =>
      selectedConversation ? toConversationContact(selectedConversation) : null,
    [selectedConversation],
  );

  const {
    messages: serverMessages,
    fetchNextPage: fetchOlderMessages,
    hasNextPage: hasOlderMessages,
    isFetchingNextPage: isLoadingOlderMessages,
    isLoading: isLoadingMessages,
  } = useGetConversationMessages(
    { conversationId: selectedConversationId ?? "" },
    { enabled: Boolean(selectedConversationId) },
  );

  const chatMessages = useMemo(
    () => (serverMessages ?? []).map(toChatMessage),
    [serverMessages],
  );

  const markReadMutation = useMarkReadConversation({
    onSuccess: (updated) => {
      queryClient.setQueriesData<ConversationsInfinite>(
        { queryKey: [SUPPORT_QUERY_KEY, "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              data: page.data.map((c) =>
                c.id === updated.id ? { ...c, unreadCount: 0 } : c,
              ),
            })),
          };
        },
      );
    },
  });

  useEffect(() => {
    setOpenSupportConversation(selectedConversationId);
    return () => setOpenSupportConversation(null);
  }, [selectedConversationId]);

  useEffect(() => {
    if (!selectedConversationId) return;
    const conv = conversations.find((c) => c.id === selectedConversationId);
    const wasUnread = conv?.unreadCount ?? 0;
    markReadMutation.mutate(selectedConversationId);
    if (wasUnread > 0) decrementSupportUnread(wasUnread);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversationId]);

  // Auto-mark-read on each new inbound when the chat is open on screen.
  // Without this, the row would show unread=1 momentarily after a new
  // message arrives while the admin is already looking at it.
  useEffect(() => {
    if (!selectedConversationId) return;
    const conv = conversations.find((c) => c.id === selectedConversationId);
    if (!conv || (conv.unreadCount ?? 0) === 0) return;
    markReadMutation.mutate(selectedConversationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    conversations.find((c) => c.id === selectedConversationId)?.lastInboundAt,
  ]);

  const sendMessageMutation = useSendMessage({
    onMutate: ({ conversationId, text, imageUrl, audioUrl }) => {
      const optimisticId = buildOptimisticId(conversationId);
      const optimistic: InAppMessage = {
        id: optimisticId,
        conversationId,
        direction: InAppMessageDirection.Outbound,
        sender: InAppMessageSender.Admin,
        senderAdminId: null,
        inResponseToMessageId: null,
        messageType: audioUrl
          ? InAppMessageType.Voice
          : imageUrl
            ? InAppMessageType.Image
            : InAppMessageType.Text,
        text: text ?? null,
        mediaUrl: audioUrl ?? imageUrl ?? null,
        mediaName: null,
        createdAt: new Date().toISOString(),
      };
      queryClient.setQueriesData<MessagesInfinite>(
        {
          queryKey: [SUPPORT_QUERY_KEY, "messages", conversationId],
        },
        (current) => insertOrReplaceMessage(current, optimistic),
      );
      return { optimisticId };
    },
    onSuccess: (message) => {
      queryClient.setQueriesData<MessagesInfinite>(
        {
          queryKey: [SUPPORT_QUERY_KEY, "messages", message.conversationId],
        },
        (current) => insertOrReplaceMessage(current, message),
      );
    },
    onError: (_err, _vars, context) => {
      const optimisticId = (context as { optimisticId?: string } | undefined)
        ?.optimisticId;
      if (!optimisticId || !selectedConversationId) return;
      queryClient.setQueriesData<MessagesInfinite>(
        {
          queryKey: [SUPPORT_QUERY_KEY, "messages", selectedConversationId],
        },
        (current) => removeMessageById(current, optimisticId),
      );
    },
  });

  const setModeMutation = useSetConversationMode({
    onSuccess: (updated) => {
      queryClient.setQueriesData<ConversationsInfinite>(
        { queryKey: [SUPPORT_QUERY_KEY, "list"] },
        (current) => {
          if (!current) return current;
          return {
            ...current,
            pages: current.pages.map((page) => ({
              ...page,
              data: page.data.map((c) =>
                c.id === updated.id
                  ? {
                      ...c,
                      status: updated.status,
                      aiEnabled: updated.aiEnabled,
                      assignedAdminId: updated.assignedAdminId,
                    }
                  : c,
              ),
            })),
          };
        },
      );
    },
  });

  const handleSend = (payload: {
    text?: string;
    imageUrl?: string;
    audioUrl?: string;
  }) => {
    if (!selectedConversationId) return;
    sendMessageMutation.mutate({
      conversationId: selectedConversationId,
      ...payload,
    });
  };

  const handleModeChange = (next: ConversationMode) => {
    if (!selectedConversationId) return;
    setModeMutation.mutate({
      conversationId: selectedConversationId,
      mode: next,
    });
  };

  const ownedByOtherAdmin = Boolean(
    selectedConversation &&
    selectedConversation.status === InAppConversationStatus.Human &&
    selectedConversation.assignedAdminId &&
    selectedConversation.assignedAdminId !== currentAdminId,
  );

  const composerDisabled = selectedConversation
    ? selectedConversation.status !== InAppConversationStatus.Human ||
      ownedByOtherAdmin
    : true;

  const composerDisabledReason = ownedByOtherAdmin
    ? t("Another admin has taken over this conversation.")
    : t(
        "AI is handling this conversation. Turn it off to send a message manually.",
      );

  return (
    <div className={styles.workspace}>
      <div className={styles.card}>
        <aside className={styles.sidebar}>
          <ConversationsList
            conversations={conversationContacts}
            selectedId={selectedConversationId}
            onSelect={(id) => setSelectedConversationId(id)}
            onLoadMore={() => fetchNextConversationsPage()}
            hasMore={hasMoreConversations}
            isLoadingMore={isLoadingMoreConversations}
            isLoading={isLoadingConversations}
            search={searchInput}
            onSearchChange={setSearchInput}
            searchPlaceholder="Search by name or phone..."
            status={statusFilter}
            onStatusChange={setStatusFilter}
            hideChannel
          />
        </aside>

        <main className={styles.main}>
          {selectedContact && selectedConversation ? (
            <ChatView
              contact={selectedContact}
              messages={chatMessages}
              mode={toToggleMode(selectedConversation.status)}
              onModeChange={handleModeChange}
              toggleDisabled={ownedByOtherAdmin}
              togglingMode={setModeMutation.isLoading}
              onSend={handleSend}
              sending={sendMessageMutation.isLoading}
              composerDisabled={composerDisabled}
              composerDisabledReason={composerDisabledReason}
              onLoadOlder={() => fetchOlderMessages()}
              hasOlder={hasOlderMessages}
              isLoadingOlder={isLoadingOlderMessages}
              isLoadingMessages={isLoadingMessages}
              aiTyping={isTyping(selectedConversation.id)}
            />
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default Support;
